import { useCallback, useEffect, useMemo, useRef } from 'react';
import { kpiConsecutiveFailApi } from '@/services/kpi-consecutive-fail/kpi-consecutive-fail-api';
import { useCanhBaoKPIBaKyLienTiepEnabled } from '@/hooks/useCanhBaoKPIBaKyLienTiepEnabled';
import { useGetPhanHe } from '@/hooks/useGetPhanHe';
import { usePrivateStore } from '@/store/usePrivateStore';
import { useKPIConsecutiveFailStore } from '@/store/kpiConsecutiveFailStore';
import CommonJs from '@/utils/commonJs';
import { CONSTANTS } from '@/utils/constants';
import type { RequestHeader } from '@/types/base';
import type { KPIConsecutiveFailScanResponse } from '@/types/kpi-consecutive-fail';

function buildScanRequestHeader(
  header: RequestHeader | null | undefined,
  year: number,
  giaiDoanId?: string | null
): RequestHeader {
  return {
    ...header,
    phanHe: header?.phanHe || 'bsc',
    selectPeriod: {
      ...header?.selectPeriod,
      year,
      idGiaiDoan: header?.selectPeriod?.idGiaiDoan ?? giaiDoanId ?? undefined,
    },
    selectTree: {
      ...header?.selectTree,
      idSelected: header?.selectTree?.idSelected,
      idParentSelected: header?.selectTree?.idParentSelected,
      isEmployee: header?.selectTree?.isEmployee ?? false,
      isCompany: header?.selectTree?.isCompany ?? false,
    },
  };
}

export function useKPIConsecutiveFailScan(enabled = true) {
  const { enabled: configEnabled, loading: configLoading } = useCanhBaoKPIBaKyLienTiepEnabled();
  const phanHe = useGetPhanHe();
  const isBsc = String(phanHe || '').toLowerCase() === CONSTANTS.PHAN_HE.BSC;
  // Chỉ quét/hiển thị khi: bật cấu hình 3 kỳ + đang ở phân hệ BSC
  const effectiveEnabled = enabled && !configLoading && configEnabled && isBsc;
  const { getRequestHeader, giaiDoan } = usePrivateStore();
  const header = getRequestHeader();
  const departmentId = header?.selectTree?.idSelected || '';
  const year = giaiDoan.year ?? header?.selectPeriod?.year ?? new Date().getFullYear();

  const data = useKPIConsecutiveFailStore((s) => s.data);
  const loading = useKPIConsecutiveFailStore((s) => s.loading);
  const syncedAt = useKPIConsecutiveFailStore((s) => s.syncedAt);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (!effectiveEnabled) {
      useKPIConsecutiveFailStore.getState().clear();
    }
  }, [effectiveEnabled]);

  const scan = useCallback(
    async (opts?: { sendNotification?: boolean; force?: boolean }) => {
      const currentHeader = getRequestHeader();
      const currentDeptId = currentHeader?.selectTree?.idSelected || '';
      const currentYear =
        giaiDoan.year ?? currentHeader?.selectPeriod?.year ?? new Date().getFullYear();

      if (
        !effectiveEnabled ||
        !currentDeptId ||
        CommonJs.checkNullIDSelectRequestHeader(currentHeader)
      ) {
        useKPIConsecutiveFailStore.getState().clear();
        return null;
      }

      const store = useKPIConsecutiveFailStore.getState();
      // Cache ngắn ~2 phút cùng scope — tránh spam khi chuyển tab trong cùng trang
      if (
        !opts?.force &&
        store.departmentId === currentDeptId &&
        store.year === currentYear &&
        store.syncedAt > 0 &&
        Date.now() - store.syncedAt < 120_000
      ) {
        return store.data;
      }

      const requestHeader = buildScanRequestHeader(currentHeader, currentYear, giaiDoan.id);
      const seq = ++fetchSeqRef.current;
      store.setLoading(true);

      try {
        const payload = await kpiConsecutiveFailApi.scan({
          requestHeader,
          idBoPhan: currentDeptId,
          year: currentYear,
          phanHe: requestHeader.phanHe || 'bsc',
          // Header icon riêng (giống cảnh báo chỉ tiêu) — không đẩy vào chuông NotifyDrawer
          sendNotification: opts?.sendNotification ?? false,
        });
        if (fetchSeqRef.current !== seq) return payload;
        useKPIConsecutiveFailStore.getState().setFromScan(currentDeptId, currentYear, payload);
        return payload;
      } catch {
        if (fetchSeqRef.current !== seq) return null;
        useKPIConsecutiveFailStore.getState().clear();
        return null;
      }
    },
    [effectiveEnabled, departmentId, year, giaiDoan.id, giaiDoan.year, getRequestHeader]
  );

  useEffect(() => {
    if (!effectiveEnabled) return;
    void scan();
  }, [effectiveEnabled, scan, departmentId, year]);

  const activeCount = useMemo(() => data?.activeCount ?? 0, [data]);

  const flatAlerts = useMemo(() => {
    if (!data) return [];
    return [
      ...data.self,
      ...data.employees.flatMap((g) => g.alerts),
      ...data.childDepts.flatMap((g) => g.alerts),
    ];
  }, [data]);

  return {
    data: data as KPIConsecutiveFailScanResponse,
    loading,
    activeCount,
    flatAlerts,
    scan,
    departmentId,
    year,
    syncedAt,
    configEnabled,
    configLoading,
    isBsc,
    featureEnabled: effectiveEnabled,
  };
}
